import { useFetchEntriesByTimeCard } from '@hooks/useEntries'
import { useHolidaysByCalendar } from '@hooks/useHolidays'
import { useAbsences } from '@hooks/useAbsences'
import { useWeekScope } from '@hooks/useWeekScope'
import { useDateTimeToLocaleString } from '@hooks/useDateTimeWithLocale'
import { dateIntersectsAbsence } from '@utils/absence'
import { Spinner } from 'components/loaders'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { DateTime, Interval } from 'luxon'
import { HourAllocationTable } from './HourAllocationTable'
import { HolidayItem } from './HolidayItem'
import { AbsenceItem } from './AbsenceItem'

interface Props {
  date: DateTime
  timeCardId?: number
  userId?: number
  holidayCalendarId?: string
}

export const DetailsTab: FC<Props> = ({
  date,
  timeCardId = 0,
  userId,
  holidayCalendarId,
}) => {
  const { t } = useTranslation()
  const toLocaleString = useDateTimeToLocaleString()
  const { end, start } = useWeekScope()

  const entriesQuery = useFetchEntriesByTimeCard(timeCardId)
  const holidaysQuery = useHolidaysByCalendar({ holidayCalendarId, start, end })
  const absencesQuery = useAbsences({
    userId: userId ?? 0,
    start,
    end,
    enabled: userId !== undefined,
  })

  const isLoading =
    entriesQuery.isInitialLoading ||
    holidaysQuery.isInitialLoading ||
    absencesQuery.isInitialLoading

  if (isLoading)
    return (
      <div className="flex flex-row items-center justify-center pt-4">
        <Spinner />
      </div>
    )

  const holidays = (holidaysQuery.data ?? []).filter(({ startDate, endDate }) =>
    Interval.fromDateTimes(startDate, endDate.endOf('day')).contains(date),
  )
  const absences = (absencesQuery.data ?? []).filter((absence) =>
    dateIntersectsAbsence(date, absence),
  )

  const hasTimeCard = timeCardId !== 0
  const isEmpty = !hasTimeCard && holidays.length === 0 && absences.length === 0

  return (
    <>
      <h2 className="mb-2 font-medium text-neutral-900">
        {toLocaleString(DateTime.DATE_HUGE)(date)}
      </h2>
      {hasTimeCard && <HourAllocationTable timeCardId={timeCardId} />}
      {(holidays.length > 0 || absences.length > 0) && (
        <div className="flex flex-col gap-2 mt-4">
          {holidays.map((holiday) => (
            <HolidayItem key={`holiday-${holiday.name}`} holiday={holiday} />
          ))}
          {absences.map((absence, index) => (
            <AbsenceItem
              key={`absence-${absence.type}-${index}`}
              absence={absence}
            />
          ))}
        </div>
      )}
      {isEmpty && (
        <div className="italic text-center text-neutral-400">
          {t('common.noTimeCardHolidayOrAbsences')}
        </div>
      )}
    </>
  )
}
